import { isJust, Maybe as StructuredMaybe } from '@execonline-inc/structured-maybe';
import { Maybe } from 'maybeasy';
import { AppyError } from '../../../../../../Appy';
import { Error } from '../../../../../../ErrorHandling';
import { MissingLinkError } from '../../../../../../LinkyLinky';
import { Link } from '../../../../../../Resource/Types';
import { LearningCollectionResource } from '../../../../Types';

export type PublishingError = AppyError | MissingLinkError | Error;

export interface TimeZone {
  fullName: string;
  name: string;
  code: TimeZoneCode;
}

export type TimeZoneCode =
  | 'Etc/GMT+12'
  | 'Pacific/Pago_Pago'
  | 'Pacific/Midway'
  | 'Pacific/Honolulu'
  | 'America/Juneau'
  | 'America/Los_Angeles'
  | 'America/Tijuana'
  | 'America/Phoenix'
  | 'America/Chihuahua'
  | 'America/Mazatlan'
  | 'America/Denver'
  | 'America/Guatemala'
  | 'America/Chicago'
  | 'America/Mexico_City'
  | 'America/Monterrey'
  | 'America/Regina'
  | 'America/Bogota'
  | 'America/New_York'
  | 'America/Indiana/Indianapolis'
  | 'America/Lima'
  | 'America/Halifax'
  | 'America/Caracas'
  | 'America/Guyana'
  | 'America/La_Paz'
  | 'America/Puerto_Rico'
  | 'America/Santiago'
  | 'America/St_Johns'
  | 'America/Sao_Paulo'
  | 'America/Argentina/Buenos_Aires'
  | 'America/Godthab'
  | 'America/Montevideo'
  | 'Atlantic/South_Georgia'
  | 'Atlantic/Azores'
  | 'Atlantic/Cape_Verde'
  | 'Europe/London'
  | 'Europe/Lisbon'
  | 'Africa/Monrovia'
  | 'Etc/UTC'
  | 'Europe/Amsterdam'
  | 'Europe/Belgrade'
  | 'Europe/Berlin'
  | 'Europe/Zurich'
  | 'Europe/Bratislava'
  | 'Europe/Brussels'
  | 'Europe/Budapest'
  | 'Africa/Casablanca'
  | 'Europe/Copenhagen'
  | 'Europe/Dublin'
  | 'Europe/Ljubljana'
  | 'Europe/Madrid'
  | 'Europe/Paris'
  | 'Europe/Prague'
  | 'Europe/Rome'
  | 'Europe/Sarajevo'
  | 'Europe/Skopje'
  | 'Europe/Stockholm'
  | 'Europe/Vienna'
  | 'Europe/Warsaw'
  | 'Africa/Algiers'
  | 'Europe/Zagreb'
  | 'Europe/Athens'
  | 'Europe/Bucharest'
  | 'Africa/Cairo'
  | 'Africa/Harare'
  | 'Europe/Helsinki'
  | 'Asia/Jerusalem'
  | 'Europe/Kaliningrad'
  | 'Europe/Kiev'
  | 'Africa/Johannesburg'
  | 'Europe/Riga'
  | 'Europe/Sofia'
  | 'Europe/Tallinn'
  | 'Europe/Vilnius'
  | 'Asia/Baghdad'
  | 'Europe/Istanbul'
  | 'Asia/Kuwait'
  | 'Europe/Minsk'
  | 'Europe/Moscow'
  | 'Africa/Nairobi'
  | 'Asia/Riyadh'
  | 'Europe/Volgograd'
  | 'Asia/Tehran'
  | 'Asia/Muscat'
  | 'Asia/Baku'
  | 'Europe/Samara'
  | 'Asia/Tbilisi'
  | 'Asia/Yerevan'
  | 'Asia/Kabul'
  | 'Asia/Yekaterinburg'
  | 'Asia/Karachi'
  | 'Asia/Tashkent'
  | 'Asia/Kolkata'
  | 'Asia/Colombo'
  | 'Asia/Kathmandu'
  | 'Asia/Almaty'
  | 'Asia/Dhaka'
  | 'Asia/Urumqi'
  | 'Asia/Rangoon'
  | 'Asia/Bangkok'
  | 'Asia/Jakarta'
  | 'Asia/Krasnoyarsk'
  | 'Asia/Novosibirsk'
  | 'Asia/Shanghai'
  | 'Asia/Chongqing'
  | 'Asia/Hong_Kong'
  | 'Asia/Irkutsk'
  | 'Asia/Kuala_Lumpur'
  | 'Australia/Perth'
  | 'Asia/Singapore'
  | 'Asia/Taipei'
  | 'Asia/Ulaanbaatar'
  | 'Asia/Tokyo'
  | 'Asia/Seoul'
  | 'Asia/Tokyo'
  | 'Asia/Yakutsk'
  | 'Australia/Adelaide'
  | 'Australia/Darwin'
  | 'Australia/Brisbane'
  | 'Australia/Melbourne'
  | 'Pacific/Guam'
  | 'Australia/Hobart'
  | 'Pacific/Port_Moresby'
  | 'Australia/Sydney'
  | 'Asia/Vladivostok'
  | 'Asia/Magadan'
  | 'Pacific/Noumea'
  | 'Pacific/Guadalcanal'
  | 'Asia/Srednekolymsk'
  | 'Pacific/Auckland'
  | 'Pacific/Fiji'
  | 'Asia/Kamchatka'
  | 'Pacific/Majuro'
  | 'Pacific/Chatham'
  | 'Pacific/Tongatapu'
  | 'Pacific/Apia'
  | 'Pacific/Fakaofo';

export interface Waiting {
  kind: 'waiting';
}

export interface Ready {
  kind: 'ready';
  publishLink: Maybe<Link>;
  publishing: LearningCollectionResource;
}

export interface Publishing {
  kind: 'publishing';
  publishLink: Maybe<Link>;
  publishing: LearningCollectionResource;
}

export interface ErrorState {
  kind: 'error';
  error: PublishingError;
  publishLink: Maybe<Link>;
  publishing: LearningCollectionResource;
}

export type PublishingState = Waiting | Ready | Publishing | ErrorState;

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const ready = (publishing: LearningCollectionResource, publishLink: Maybe<Link>): Ready => ({
  kind: 'ready',
  publishLink,
  publishing,
});

export const published = (state: Ready): Publishing => ({
  kind: 'publishing',
  publishLink: state.publishLink,
  publishing: state.publishing,
});

export const error = (error: PublishingError, state: Ready | Publishing): ErrorState => ({
  kind: 'error',
  publishLink: state.publishLink,
  publishing: state.publishing,
  error,
});

export const isPublished = (publishedAt: StructuredMaybe<Date>): boolean => isJust(publishedAt);
